import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSectionById, createSection, updateSection, getBranches } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function AddEditSection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("sections");
  const { t: tCommon } = useTranslation("common");

  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    branch_id: "",
    name: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getBranches(token).then(setBranches).catch(() => notify.error(t("notify_load_branches_failed", "Failed to load branches")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id) {
      getSectionById(token, id).then((sec) => {
        setForm({
          branch_id: sec.branch_id ? String(sec.branch_id) : "",
          name: sec.name || "",
          is_active: sec.is_active !== undefined ? sec.is_active : true,
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load section")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = branches.map((b) => ({ value: String(b.id), label: b.name }));

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.branch_id) newErrors.branch_id = t("validation_branch_required", "Branch is required");
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Section name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      branch_id: Number(form.branch_id),
      name: form.name.trim(),
      is_active: form.is_active,
    };

    try {
      if (id) {
        await updateSection(token, id, payload);
        notify.success(t("notify_updated", "Section updated successfully"));
      } else {
        await createSection(token, payload);
        notify.success(t("notify_created", "Section created successfully"));
      }
      navigate("/sections");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/sections")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Section") : t("add_title", "Add New Section")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "A named dining area — group its tables under it on the Tables screen")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <SelectBox label={t("field_branch_label", "Branch")} options={branchOptions} value={form.branch_id} onChange={(val) => setForm((prev) => ({ ...prev, branch_id: val }))} error={errors.branch_id} icon={ICONS.Branches} />
          <FloatingInput label={t("field_name_label", "Section Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Sections} />

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_section", "Update Section") : t("btn_create_section", "Create Section")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/sections")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
